/* eslint-disable jsx-a11y/anchor-is-valid */
/* eslint-disable array-callback-return */
/* eslint-disable react-hooks/exhaustive-deps */
import React, { useContext, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { AppContext } from '../context/AppContext'
import OrderService from '../services/OrderService'
import clsx from 'clsx'

const OrderList = () => {
    const { currentUser } = useContext(AppContext)
    const [orders, setOrders] = useState([])
    const [loading, setLoading] = useState(false)
    const [searchTerm, setSearchTerm] = useState('')
    const [filterStatus, setFilterStatus] = useState('all')
    const [filterVendor, setFilterVendor] = useState('all')
    const [currentPage, setCurrentPage] = useState(1)
    const [itemsPerPage, setItemsPerPage] = useState(10)
    const [selectedOrder, setSelectedOrder] = useState(null)
    const [showStatusModal, setShowStatusModal] = useState(false)
    const [statusLoading, setStatusLoading] = useState(false)
    const [newStatus, setNewStatus] = useState('')
    const [statusRemarks, setStatusRemarks] = useState('')
    const [dateFilter, setDateFilter] = useState({
        startDate: '',
        endDate: ''
    })
    
    const [pagination, setPagination] = useState({
        current_page: 1,
        per_page: 10,
        total: 0,
        total_pages: 0,
        has_next_page: false,
        has_prev_page: false,
        from: 0,
        to: 0
    })

    useEffect(() => {
        fetchOrders()
    }, [currentPage, searchTerm, filterStatus, filterVendor, itemsPerPage, dateFilter])

    const fetchOrders = async () => {
        setLoading(true)
        
        try {
            const params = {
                offset: (currentPage - 1) * itemsPerPage,
                limit: itemsPerPage,
                globalSearch: searchTerm,
                orderBy: 'order_id',
                orderType: 'desc'
            }

            // Add date filter
            if (dateFilter.startDate && dateFilter.endDate) {
                params.columns = {
                    orderDate: [
                        { filterValue: dateFilter.startDate },
                        { filterValue: dateFilter.endDate }
                    ]
                }
            }

            // Add status filter
            if (filterStatus && filterStatus !== 'all') {
                params.columns = {
                    ...params.columns,
                    status: [{ filterValue: filterStatus, filterType: '=' }]
                }
            }

            const response = await OrderService.getOrdersList(params)
            
            if (response.success) {
                setOrders(response.data || [])
                
                // Calculate pagination from the response
                const totalCount = response.count || 0
                setPagination({
                    current_page: currentPage,
                    per_page: itemsPerPage,
                    total: totalCount,
                    total_pages: Math.ceil(totalCount / itemsPerPage),
                    has_next_page: currentPage < Math.ceil(totalCount / itemsPerPage),
                    has_prev_page: currentPage > 1,
                    from: (currentPage - 1) * itemsPerPage + 1,
                    to: Math.min(currentPage * itemsPerPage, totalCount)
                })
            } else {
                console.error('Failed to fetch orders:', response.errorMessage || response.message)
                setOrders([])
            }
        } catch (error) {
            console.error('Error fetching orders:', error)
            setOrders([])
        } finally {
            setLoading(false)
        }
    }

    // Handle search with debounce
    const [searchDebounceTimer, setSearchDebounceTimer] = useState(null)
    
    const handleSearchChange = (value) => {
        setSearchTerm(value)
        
        // Clear previous timer
        if (searchDebounceTimer) {
            clearTimeout(searchDebounceTimer)
        }
        
        // Set new timer for debounced search
        const timer = setTimeout(() => {
            setCurrentPage(1) // Reset to first page when searching
        }, 500)
        
        setSearchDebounceTimer(timer)
    }

    const handleFilterChange = (type, value) => {
        if (type === 'status') {
            setFilterStatus(value)
        } else if (type === 'vendor') {
            setFilterVendor(value)
        } else if (type === 'perPage') {
            setItemsPerPage(parseInt(value))
        }
        setCurrentPage(1) // Reset to first page when filtering
    }

    const handleDateFilterChange = (type, value) => {
        setDateFilter(prev => ({
            ...prev,
            [type]: value
        }))
        setCurrentPage(1)
    }

    const handlePageChange = (page) => {
        setCurrentPage(page)
        window.scrollTo({ top: 0, behavior: 'smooth' })
    }

    const getStatusBadge = (status) => {
        const statusInfo = OrderService.formatOrderStatus(status)
        return `badge-${statusInfo.color}`
    }

    const refreshOrders = () => {
        setCurrentPage(1)
        setSearchTerm('')
        setFilterStatus('all')
        setFilterVendor('all')
        setDateFilter({ startDate: '', endDate: '' })
        fetchOrders()
    }

    const openStatusModal = (order) => {
        setSelectedOrder(order)
        setNewStatus(order.status)
        setStatusRemarks('')
        setShowStatusModal(true)
    }

    const closeStatusModal = () => {
        setShowStatusModal(false)
        setSelectedOrder(null)
        setNewStatus('')
        setStatusRemarks('')
    }

    const handleStatusChange = async () => {
        if (!selectedOrder || !newStatus) {
            alert('Please select a status')
            return
        }

        setStatusLoading(true)
        
        try {
            const response = await OrderService.changeOrderStatus({
                orderId: selectedOrder.orderId,
                orderStatus: newStatus,
                remarks: statusRemarks
            })

            if (response.success) {
                // Update the order in the list
                setOrders(orders.map(order => 
                    order.orderId === selectedOrder.orderId 
                        ? { ...order, status: newStatus }
                        : order
                ))
                closeStatusModal()
                alert('Order status updated successfully!')
            } else {
                alert('Failed to update order status: ' + (response.errorMessage || response.message))
            }
        } catch (error) {
            console.error('Error updating order status:', error)
            alert('Error updating order status. Please try again.')
        } finally {
            setStatusLoading(false)
        }
    }

    const handlePrintOrder = async (orderId) => {
        try {
            await OrderService.printOrder(orderId)
        } catch (error) {
            console.error('Error printing order:', error)
            alert('Error printing order. Please try again.')
        }
    }

    const renderPagination = () => {
        if (pagination.total_pages <= 1) return null

        const pages = []
        const maxVisiblePages = 5
        const startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2))
        const endPage = Math.min(pagination.total_pages, startPage + maxVisiblePages - 1)

        for (let i = startPage; i <= endPage; i++) {
            pages.push(
                <button
                    key={i}
                    className={clsx('btn btn-sm mx-1', {
                        'btn-primary': i === currentPage,
                        'btn-light': i !== currentPage
                    })}
                    onClick={() => handlePageChange(i)}
                >
                    {i}
                </button>
            )
        }

        return (
            <div className="d-flex justify-content-center align-items-center mt-4">
                <button
                    className="btn btn-sm btn-light mx-1"
                    disabled={!pagination.has_prev_page}
                    onClick={() => handlePageChange(currentPage - 1)}
                >
                    Previous
                </button>
                {pages}
                <button
                    className="btn btn-sm btn-light mx-1"
                    disabled={!pagination.has_next_page}
                    onClick={() => handlePageChange(currentPage + 1)}
                >
                    Next
                </button>
            </div>
        )
    }

    return (
        <div className="container-fluid">
            {/* Page Header */}
            <div className="d-flex justify-content-between align-items-center mb-4">
                <div>
                    <h1 className="h3 mb-0">Order Management</h1>
                    <p className="text-muted">Manage customer orders and track their status</p>
                </div>
                <Link to="/sales-orders/create" className="btn btn-primary">
                    <i className="fas fa-plus me-2"></i>Create New Order
                </Link>
            </div>

            {/* Filters */}
            <div className="card mb-4">
                <div className="card-body">
                    <div className="row g-3">
                        {/* Search */}
                        <div className="col-md-3">
                            <label className="form-label">Search</label>
                            <input
                                type="text"
                                className="form-control"
                                placeholder="Search by order no, customer name..."
                                value={searchTerm}
                                onChange={(e) => handleSearchChange(e.target.value)}
                            />
                        </div>

                        {/* Status Filter */}
                        <div className="col-md-2">
                            <label className="form-label">Status</label>
                            <select
                                className="form-select"
                                value={filterStatus}
                                onChange={(e) => handleFilterChange('status', e.target.value)}
                            >
                                <option value="all">All Status</option>
                                {OrderService.getOrderStatusOptions().map(status => (
                                    <option key={status.value} value={status.value}>
                                        {status.label}
                                    </option>
                                ))}
                            </select>
                        </div>

                        {/* Date Range */}
                        <div className="col-md-2">
                            <label className="form-label">Start Date</label>
                            <input
                                type="date"
                                className="form-control"
                                value={dateFilter.startDate}
                                onChange={(e) => handleDateFilterChange('startDate', e.target.value)}
                            />
                        </div>
                        <div className="col-md-2">
                            <label className="form-label">End Date</label>
                            <input
                                type="date"
                                className="form-control"
                                value={dateFilter.endDate}
                                onChange={(e) => handleDateFilterChange('endDate', e.target.value)}
                            />
                        </div>

                        {/* Items per page */}
                        <div className="col-md-2">
                            <label className="form-label">Per Page</label>
                            <select
                                className="form-select"
                                value={itemsPerPage}
                                onChange={(e) => handleFilterChange('perPage', e.target.value)}
                            >
                                <option value="10">10</option>
                                <option value="25">25</option>
                                <option value="50">50</option>
                                <option value="100">100</option>
                            </select>
                        </div>

                        {/* Refresh Button */}
                        <div className="col-md-1 d-flex align-items-end">
                            <button
                                className="btn btn-outline-secondary"
                                onClick={refreshOrders}
                                title="Refresh"
                            >
                                <i className="fas fa-sync-alt"></i>
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Orders Table */}
            <div className="card">
                <div className="card-body">
                    {loading ? (
                        <div className="text-center py-5">
                            <div className="spinner-border" role="status">
                                <span className="visually-hidden">Loading...</span>
                            </div>
                            <p className="mt-2">Loading orders...</p>
                        </div>
                    ) : (
                        <>
                            <div className="table-responsive">
                                <table className="table table-hover">
                                    <thead className="table-light">
                                        <tr>
                                            <th>Order ID</th>
                                            <th>Order No</th>
                                            <th>Customer</th>
                                            <th>Vendor</th>
                                            <th>Order Date</th>
                                            <th>Grand Total</th>
                                            <th>Status</th>
                                            <th>Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {orders.length > 0 ? (
                                            orders.map((order) => (
                                                <tr key={order.orderId}>
                                                    <td>#{order.orderId}</td>
                                                    <td>
                                                        <Link 
                                                            to={`/sales-orders/${order.orderId}`}
                                                            className="text-decoration-none fw-bold"
                                                        >
                                                            {order.orderNo}
                                                        </Link>
                                                    </td>
                                                    <td>{order.customerName || 'N/A'}</td>
                                                    <td>{order.vendorName || 'N/A'}</td>
                                                    <td>{OrderService.formatDate(order.orderDate)}</td>
                                                    <td>{OrderService.formatCurrency(order.grandTotal)}</td>
                                                    <td>
                                                        <span className={clsx('badge', getStatusBadge(order.status))}>
                                                            {OrderService.formatOrderStatus(order.status).label}
                                                        </span>
                                                    </td>
                                                    <td>
                                                        <div className="btn-group btn-group-sm">
                                                            <Link
                                                                to={`/sales-orders/${order.orderId}`}
                                                                className="btn btn-outline-primary btn-sm"
                                                                title="View Details"
                                                            >
                                                                <i className="fas fa-eye"></i>
                                                            </Link>
                                                            <button
                                                                className="btn btn-outline-success btn-sm"
                                                                onClick={() => openStatusModal(order)}
                                                                title="Change Status"
                                                            >
                                                                <i className="fas fa-edit"></i>
                                                            </button>
                                                            <button
                                                                className="btn btn-outline-info btn-sm"
                                                                onClick={() => handlePrintOrder(order.orderId)}
                                                                title="Print Order"
                                                            >
                                                                <i className="fas fa-print"></i>
                                                            </button>
                                                            <Link
                                                                to={`/sales-invoices/create/${order.orderId}`}
                                                                className="btn btn-outline-warning btn-sm"
                                                                title="Create Invoice"
                                                            >
                                                                <i className="fas fa-file-invoice"></i>
                                                            </Link>
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))
                                        ) : (
                                            <tr>
                                                <td colSpan="8" className="text-center py-4">
                                                    <div className="text-muted">
                                                        <i className="fas fa-shopping-cart fa-3x mb-3"></i>
                                                        <p>No orders found</p>
                                                    </div>
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>

                            {/* Pagination Info */}
                            {orders.length > 0 && (
                                <div className="d-flex justify-content-between align-items-center mt-3">
                                    <div className="text-muted">
                                        Showing {pagination.from} to {pagination.to} of {pagination.total} entries
                                    </div>
                                    {renderPagination()}
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>

            {/* Status Change Modal */}
            {showStatusModal && (
                <div className="modal d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                    <div className="modal-dialog">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">Change Order Status</h5>
                                <button 
                                    type="button" 
                                    className="btn-close" 
                                    onClick={closeStatusModal}
                                ></button>
                            </div>
                            <div className="modal-body">
                                <div className="mb-3">
                                    <label className="form-label">Order: {selectedOrder?.orderNo}</label>
                                    <p className="text-muted">Current Status: {selectedOrder?.status}</p>
                                </div>
                                
                                <div className="mb-3">
                                    <label className="form-label">New Status</label>
                                    <select
                                        className="form-select"
                                        value={newStatus}
                                        onChange={(e) => setNewStatus(e.target.value)}
                                    >
                                        {OrderService.getOrderStatusOptions().map(status => (
                                            <option key={status.value} value={status.value}>
                                                {status.label}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                <div className="mb-3">
                                    <label className="form-label">Remarks (Optional)</label>
                                    <textarea
                                        className="form-control"
                                        rows="3"
                                        value={statusRemarks}
                                        onChange={(e) => setStatusRemarks(e.target.value)}
                                        placeholder="Enter any remarks about this status change..."
                                    ></textarea>
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button 
                                    type="button" 
                                    className="btn btn-secondary" 
                                    onClick={closeStatusModal}
                                >
                                    Cancel
                                </button>
                                <button 
                                    type="button" 
                                    className="btn btn-primary"
                                    onClick={handleStatusChange}
                                    disabled={statusLoading}
                                >
                                    {statusLoading ? (
                                        <>
                                            <span className="spinner-border spinner-border-sm me-2"></span>
                                            Updating...
                                        </>
                                    ) : (
                                        'Update Status'
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    )
}

export default OrderList 